import { dynamicTariffState, isDynamicScheme, validateDynamicTariff } from "./dynamicTariff";
import { ITEMIZED_TARIFF_MODE, SINGLE_ENERGY_RATE, itemizedTariffErrors, legacyFixedChargeErrors } from "./retailTariff";
import {
  VNB_BATTERY_INJECTION_CAPTION,
  VNB_BATTERY_INJECTION_HINT,
  VNB_GRID_CHARGING_CAPTION,
  VNB_GRID_CHARGING_HINT,
  VNB_INJECTION_CAPTION,
  VNB_INJECTION_HINT,
  VNB_INJECTION_RUN_NOTE,
  VNB_MODE,
  VNB_OPTION_DESCRIPTION,
  VNB_OPTION_LABEL,
  buildVirtualNetBillingPayload,
  isVirtualNetBilling,
  validateVirtualNetBilling,
} from "./virtualNetBilling";
// Behind-the-Meter (BTM) settlement — self-consumption & peak shaving.
// Mirrors the backend `BESS_feasibility/Configuration/btm.py` so the payload
// emits exactly the keys `normalize_btm_config()` expects. Keep the two in sync.
//
// A behind-the-meter system sits on the customer side of the meter and serves
// the site load. It never sells into a wholesale market, so it produces AVOIDED
// COST, not revenue. Value streams:
//
//   1. Self-consumption offset (always on) — PV and battery serve the load, so
//      that energy is not bought from the supplier. Valued at the site's retail
//      energy price for the hour (peak / non-peak, per month).
//   2. Peak shaving (optional) — C&I supply contracts bill a capacity component
//      in EUR/kW-month against the highest metered grid draw. Discharging over
//      the site peak lowers it.
//   3. Retail time-of-use arbitrage (optional) — charge from the grid off-peak,
//      discharge into the load on-peak. Requires grid charging, so it is opt-in.
//   4. Surplus export (optional) — either curtailed (no value) or sold at a
//      fixed feed-in price.
//
// Net billing / net metering with carried-over energy credits is deliberately
// NOT part of this model; the backend switches carryover off for a BTM run.
//
// Retail ENERGY prices are not duplicated here: BTM reuses the project's monthly
// peak / non-peak tariff table from Feasibility > Technical (Demand & Tariffs).
// This block only carries the BTM-specific commercial terms.

export const BTM_SETTLEMENT_VALUE = "BTM";

export const BTM_SETTLEMENT_LABEL =
  "Behind-the-Meter — Self-Consumption & Peak Shaving";

export const BTM_HELPER_TEXT =
  "For systems on the customer side of the meter that serve the site's own " +
  "consumption. The return is avoided electricity cost rather than market " +
  "revenue, so this model produces a savings report: the bill the site would " +
  "pay without the system against the bill it pays with it. Requires a demand " +
  "profile and the monthly retail tariffs on the Feasibility > Technical page.";

export const BTM_LEARN_MORE_TEXT =
  "Savings are valued at the retail price applying in each individual hour, " +
  "not at an annual average. Self-consumption is always included; peak " +
  "shaving, retail time-of-use arbitrage and surplus export are switched on " +
  "individually below. No wholesale market, capacity or balancing revenue is " +
  "modelled — a behind-the-meter system is settled against the retail tariff " +
  "only, which makes the result deliberately conservative.";

export const DEMAND_CHARGE_BASIS_OPTIONS = [
  {
    value: "monthly_peak",
    label: "Highest metered grid draw in the month (any hour)",
    description:
      "The capacity charge is billed against the single highest grid draw " +
      "recorded anywhere in the period, at any hour of the day. This is how " +
      "Germany's Leistungspreis is measured. Note that the Greek MV " +
      "transmission capacity charge does NOT work this way - it measures the " +
      "peak window only, the option below.",
  },
  {
    value: "peak_window",
    label: "Highest metered grid draw inside the tariff peak window only",
    description:
      "The capacity charge is billed against the highest grid draw recorded " +
      "inside the tariff peak window only; draw outside that window is not " +
      "measured for capacity purposes. This is the Greek MV arrangement: the " +
      "transmission capacity charge is the maximum monthly value of the mean " +
      "hourly demand during peak hours (11:00-14:00, varying by season).",
  },
];

export const DEMAND_CHARGE_PERIOD_OPTIONS = [
  {
    value: "monthly",
    label: "Highest demand in each calendar month",
    description:
      "The maximum resets on the first of every month and the charge is billed " +
      "twelve times a year. Greek MV and most Southern European C&I contracts " +
      "work this way. The rate is entered in EUR/kW-month.",
  },
  {
    value: "annual",
    label: "Single highest demand in the year",
    description:
      "The maximum never resets: one bad hour in January sets the charge for " +
      "all twelve months. Germany's Leistungspreis works this way and is quoted " +
      "in EUR/kW-year. Beating one peak a year is far harder than beating " +
      "twelve, so the saving is smaller than a monthly scheme would suggest.",
  },
];

export const DEMAND_CHARGE_INTERVAL_OPTIONS = [
  {
    value: "hourly",
    label: "Mean demand over each hour",
    description:
      "The billed figure is the average demand across a clock hour. This is " +
      "how the Greek MV transmission capacity charge is defined, and it " +
      "matches the dispatch resolution exactly.",
  },
  {
    value: "15min",
    label: "Mean demand over each 15 minutes",
    description:
      "The billed figure is the highest average demand in any quarter of an " +
      "hour, which is how Germany's Leistungspreis and most metered C&I " +
      "contracts work. Needs a 15-minute metered profile: with an hourly " +
      "profile the spikes are averaged away and the charge is understated.",
  },
];

// Capacity-charge structure by bidding zone.
//
// ONLY zones whose rules have actually been checked against a primary source
// appear here. An unverified preset is worse than none: it would put the
// authority of the platform behind a guess, and the user would have no way to
// tell which entries were researched and which were filled in by pattern.
//
// Applied on request, never silently: the zone identifies the COUNTRY, not the
// customer's voltage level or contract, and the supplier may add its own scheme
// on top of the regulated one.
export const CAPACITY_CHARGE_PRESETS = {
  GR: {
    label: "Greece — MV transmission capacity charge",
    measurementInterval: "hourly",
    measurementPeriod: "monthly",
    basis: "peak_window",
    note:
      "Greek MV is billed on the maximum monthly value of the mean HOURLY " +
      "demand, measured during peak hours only (11:00-14:00, varying by " +
      "season). Source: YPEN / ADMIE published transmission tariff. Note the " +
      "transmission element alone is only around 1.4 EUR/kW-month - take the " +
      "all-in capacity component from the site's bill.",
  },
  DE_LU: {
    label: "Germany — Leistungspreis",
    measurementInterval: "15min",
    measurementPeriod: "annual",
    basis: "monthly_peak",
    note:
      "The German Leistungspreis is billed once on the highest 15-minute " +
      "average demand of the calendar year, at any hour, quoted in EUR/kW-year " +
      "(typically 100-300). Needs a 15-minute metered profile to measure " +
      "properly. This zone also covers Luxembourg, whose rules differ - check " +
      "before applying.",
  },
};

export function capacityChargePreset(biddingZone) {
  const key = String(biddingZone || "").trim().toUpperCase();
  return CAPACITY_CHARGE_PRESETS[key] || null;
}

export const SURPLUS_TREATMENT_OPTIONS = [
  {
    value: "none",
    label: "No value — surplus generation is curtailed",
    description:
      "Generation above what the load and the battery can absorb has no " +
      "commercial value and is curtailed by the inverter. Correct wherever " +
      "export is not contracted or not permitted, and the conservative choice.",
  },
  {
    value: "fixed_price",
    label: "Sold at a fixed feed-in price (EUR/MWh)",
    description:
      "Surplus energy exported at the meter is paid at a fixed price per MWh " +
      "for the whole horizon. Export is still limited by the grid connection " +
      "capacity.",
  },
];

export const SURPLUS_PRICE_HELPER_TEXT =
  "Fixed price for exported surplus over the study period. Enter 0 for export without compensation.";
export const SURPLUS_PRICE_REQUIRED_TEXT =
  "Enter a fixed feed-in price of 0 or more (EUR/MWh). Leave no blank value; 0 means export without compensation.";
export const UNPAID_SURPLUS_EXPORT_NOTE =
  "At 0 EUR/MWh, exported surplus earns no revenue and does not offset consumption at other times. This is not zero feed-in: export remains subject to the connection limits and must be permitted by the connection terms.";

export function isUnpaidSurplusExport(btm) {
  const price = btm?.surplus?.feedInEurPerMwh;
  return !isZeroFeedIn(btm) && !isVirtualNetBilling(btm) && !btm?.netBilling?.enabled
    && btm?.surplus?.treatment === "fixed_price"
    && price != null && String(price).trim() !== "" && Number(price) === 0;
}

export const DEMAND_CHARGE_HELPER_TEXT =
  "Enter the capacity component of the site's supply contract in EUR per kW " +
  "per month. The optimizer will discharge the battery to hold down the " +
  "metered peak, and the report shows the reduction month by month. Because " +
  "dispatch is solved one day at a time, the battery cannot anticipate a " +
  "higher-demand day later in the same month, so the reduction shown is " +
  "achievable rather than theoretically optimal.";

export const TOU_ARBITRAGE_HELPER_TEXT =
  "Allows the battery to be charged from the grid during the non-peak tariff " +
  "band and discharged into the load during the peak band, saving the " +
  "difference. The cost of the imported energy is charged in full against the " +
  "saving. Leave off where the connection terms or a self-consumption support " +
  "scheme forbid grid charging — the battery then charges from surplus " +
  "generation only.";

// ---------------------------------------------------------------------------
// Battery wear priced in the dispatch. Mirrors the backend btm.py wearCost
// block: a shadow price per MWh DISCHARGED that the optimiser pays for using
// the battery, so it does not cycle for a gain smaller than the wear it
// causes (a thin night-to-day tariff spread, for instance). It is NOT a cash
// flow: the saving in the report stays the metered bill difference, and
// ageing reaches the cash flows through the retention curve and augmentation.
// The default is the same rate the platform's market side already charges
// its bids and P&L with: battery price / (warranted cycles x usable energy).
// ---------------------------------------------------------------------------
export const WEAR_COST_HELPER_TEXT =
  "What the dispatch pays, per MWh the battery discharges, for the cycle life " +
  "it uses up, so the battery is not cycled for a gain smaller than the wear. " +
  "It is a price inside the optimisation, not a cash flow: the saving in the " +
  "report stays the metered bill difference, and Appendix A prints the price " +
  "and its basis. Behind-the-meter studies only.";

// The field itself sits on the BESS page; the General page says where.
export const WEAR_COST_MOVED_TEXT =
  "Set on the BESS page, under Battery Performance, beside the cycle life and " +
  "state-of-charge window the price is derived from.";

export const WEAR_COST_MODE_OPTIONS = [
  {
    value: "auto",
    label: "Derived from the battery price and warranted cycle life (default)",
    description:
      "Wear price = battery price ÷ (warranted cycles × usable energy per cycle), " +
      "in EUR per MWh discharged. Uses the catalog price or the cost override " +
      "from Feasibility > Economics, the battery's rated cycle life and its " +
      "state-of-charge window, so it moves with the battery you select.",
  },
  {
    value: "custom",
    label: "Enter a price per MWh discharged",
    description:
      "Your own figure in EUR per MWh discharged — a supplier's degradation " +
      "cost, a warranty-based estimate, or a sensitivity. Typical lithium " +
      "iron phosphate systems land between about 20 and 60 EUR/MWh.",
  },
  {
    value: "off",
    label: "Not priced — every cycle up to the cycle limit is free",
    description:
      "Restores free cycling: the optimiser will take any positive gain, " +
      "however small, and the report says wear was not priced. Use it to see " +
      "the upper bound of the saving or to reproduce an earlier study.",
  },
];

export const WEAR_COST_CUSTOM_REQUIRED_TEXT =
  "Enter a non-negative wear price in EUR per MWh discharged, or switch the " +
  "mode back to the derived default.";
export const WEAR_COST_VERY_HIGH_TEXT =
  "A wear price above 200 EUR/MWh discharged is very high and will suppress " +
  "most cycling — confirm it against the battery's price and cycle life.";
export const WEAR_COST_INDICATIVE_UNAVAILABLE_TEXT =
  "The derived price appears here once the selected battery has a price, a " +
  "rated cycle life and a state-of-charge window; the report prints the price " +
  "the run actually used.";

/**
 * The derived wear price for the battery currently selected, as the backend
 * will resolve it (BatteryRules): price over warranted throughput. Returns
 * null when the selection cannot form the basis. Indicative: the report
 * prints the figure the run used, from the same inputs.
 */
export function wearCostIndicative(cfg) {
  const selected = Array.isArray(cfg?.bess?.selected) ? cfg.bess.selected : [];
  if (!selected.length) return null;
  const activeName = cfg?.bess?.activeName;
  const battery = selected.find((b) => b?.name === activeName) || selected[0];
  const chars = battery?.details?.characteristics || {};
  const overrides = battery?.overrides || {};
  const count = Math.max(1, Number(battery?.count) || 1);
  const unitEnergyKwh = Number(battery?.details?.energy ?? chars.Energy);
  const cycles = Number(chars.N_cycles);
  const socMin = Number(overrides.soc_min ?? chars.soc_min);
  const socMax = Number(overrides.soc_max ?? chars.soc_max);
  const span = Number.isFinite(socMin) && Number.isFinite(socMax) ? (socMax - socMin) / 100 : NaN;
  // The cost override replaces the catalog price for the whole installed
  // fleet (see equipmentCostOverride); the catalog price is per pack.
  const overrideRaw = cfg?.feasibility?.economics?.equipmentCostOverride?.battery;
  const overrideEur = overrideRaw === "" || overrideRaw == null ? NaN : Number(overrideRaw);
  const fleetPriceEur = Number.isFinite(overrideEur) && overrideEur >= 0
    ? overrideEur
    : Number(chars.Price) * count;
  const usableKwh = unitEnergyKwh * count * span;
  if (![fleetPriceEur, cycles, usableKwh].every((v) => Number.isFinite(v) && v > 0)) return null;
  const eurPerKwh = fleetPriceEur / (cycles * usableKwh);
  return {
    eurPerMwh: eurPerKwh * 1000,
    fleetPriceEur,
    cycles,
    usableKwh,
    priceBasis: Number.isFinite(overrideEur) && overrideEur >= 0 ? "override" : "catalog",
    batteryName: String(battery?.name || ""),
  };
}

export const SURPLUS_HELPER_TEXT =
  "What happens to generation above what the load and the battery can absorb.";

// ---------------------------------------------------------------------------
// Grid injection regime. Zero feed-in (αυτοκατανάλωση χωρίς έγχυση) is not a
// value stream: it is the connection regime that DECIDES the surplus treatment
// and net billing. A station that never exports has no surplus to sell and
// nothing to net, so both are forced off; the backend additionally overrides
// the BESS page's connection-point injection switch to OFF. Mirrors backend btm.py.
// ---------------------------------------------------------------------------

export const GRID_INJECTION_OPTIONS = [
  {
    value: "allowed",
    label: "Export permitted — surplus settled as configured below",
    description:
      "The connection allows the station to inject. What the injected " +
      "surplus is worth is decided by the surplus treatment and the net " +
      "billing settings below.",
  },
  {
    value: "pv_only",
    label: "Export from PV only — the battery never injects",
    description:
      "The connection may export PV surplus, settled by the surplus treatment " +
      "and the net billing settings below. The battery serves the site load " +
      "only and never discharges into the grid — the position of a storage " +
      "unit on its own inverter and meter that is barred from injecting. " +
      "Grid charging follows the time-of-use stream where the framework " +
      "permits it.",
    // A standalone battery has no PV surplus to export: the option is hidden
    // for it and validateBtm rejects it (zero feed-in is the regime there).
    hybridOnly: true,
  },
  {
    value: "zero_feed_in",
    label: "Zero feed-in — no energy is exported to the grid",
    description:
      "A direction sensor at the connection point throttles the station " +
      "whenever generation would otherwise flow out to the grid, so nothing " +
      "is ever exported. Self-consumption and peak shaving are unchanged; " +
      "there is no surplus to sell and nothing to net, so the fixed feed-in " +
      "and net billing do not apply. The battery may still be charged from " +
      "the grid where the connection is on the interconnected system.",
  },
  {
    // Greece only (ΚΥΑ ΥΠΕΝ/ΔΑΠΕΕΚ/102876/1958/2026). Needs a PV station.
    value: VNB_MODE,
    label: VNB_OPTION_LABEL,
    description: VNB_OPTION_DESCRIPTION,
    hybridOnly: true,
    greeceOnly: true,
  },
];

export const GRID_INJECTION_HELPER_TEXT =
  "Whether the connection lets the station export at all. Zero feed-in is the " +
  "regime for a site that cannot or does not want to inject — a saturated " +
  "grid, no netting contract, no aggregator — and it settles the two " +
  "sections below on its own. 'Export from PV only' keeps the PV surplus " +
  "exported and settled below while the battery serves the site load only.";

// A standalone battery has no PV surplus, so its helper text does not offer
// the PV-only regime.
export const GRID_INJECTION_HELPER_TEXT_STANDALONE =
  "Whether the connection lets the station export at all. Zero feed-in is the " +
  "regime for a site that cannot or does not want to inject — a saturated " +
  "grid, no netting contract, no aggregator.";

// Export from PV only (η μπαταρία δεν εγχέει): the dispatch caps the
// battery's discharge at the residual site load, so stored energy never
// reaches the meter; the PV surplus is exported and settled as configured.
export const PV_ONLY_EXPORT_NOTE =
  "The battery serves the site load only: the dispatch caps its discharge at " +
  "the load still unserved after PV in every hour, so stored energy never " +
  "reaches the meter. The PV surplus is exported and settled as configured " +
  "below.";

export const PV_ONLY_EXPORT_TOU_NOTE =
  "Grid charging of a battery that does not inject is permitted on the " +
  "interconnected system where the storage has its own inverter and its own " +
  "meter; it is not permitted on a non-interconnected island.";

export const PV_ONLY_EXPORT_RUN_NOTE =
  "'Grid injection at the connection point' on the BESS page will be switched " +
  "ON for this run, whatever it is set to there: the PV surplus is exported. " +
  "'Battery injection' stays OFF.";

export const PV_ONLY_EXPORT_UNPRICED_TEXT =
  "Export from PV only is selected, but the surplus carries no price " +
  "(treatment 'none', net billing off), so the PV surplus will be curtailed " +
  "rather than exported. Set a fixed feed-in price or enable net billing to " +
  "value the exported PV surplus.";

export const PV_ONLY_EXPORT_STANDALONE_TEXT =
  "'Export from PV only' needs PV to export. A standalone battery that must " +
  "not inject is the zero feed-in case — select zero feed-in.";

export const ZERO_FEED_IN_SURPLUS_LOCKED_TEXT =
  "Locked by zero feed-in: the station never exports, so there is no surplus " +
  "to sell. Generation above what the load and the battery can absorb is " +
  "curtailed by the direction sensor.";

export const ZERO_FEED_IN_NET_BILLING_LOCKED_TEXT =
  "Locked by zero feed-in: net billing values the surplus injected into the " +
  "grid, and a zero feed-in station never injects.";

export const ZERO_FEED_IN_TOU_NOTE =
  "Under zero feed-in the framework lets the battery charge from the grid on " +
  "the interconnected system, so this is a commercial choice, not a " +
  "restriction of the scheme. It is not permitted on a non-interconnected " +
  "island.";

// The BESS-page switch is the connection point's, not the battery's: it
// governs PV surplus and battery discharge together. The battery's own line
// sits under it (btmBatteryInjectionState).
export const GRID_INJECTION_SWITCH_LABEL = "Grid injection at the connection point (PV + battery)";
export const BATTERY_INJECTION_SWITCH_LABEL = "Battery injection";

export const ZERO_FEED_IN_RUN_NOTE =
  "'Grid injection at the connection point' on the BESS page will be switched " +
  "OFF for this run, whatever it is set to there.";

// Documented per country, as for net billing. Greece is populated from the
// decision in force; any other country gets the generic description and the
// report labels the connection terms as an assumption to verify.
export const ZERO_FEED_IN_COUNTRY_DEFAULTS = {
  GR: {
    documented: true,
    countryLabel: "Greece",
    schemeName: "Αυτοκατανάλωση χωρίς έγχυση",
    gridChargingPermitted: true,
    note:
      "Greek zero feed-in: the station serves the site's own supply and never " +
      "injects. No netting contract, no aggregator, no registry entry and no " +
      "connection fee; the connection carries a special non-injection term " +
      "and the operator may test and monitor it. PV capacity and the battery " +
      "inverter are each limited to the agreed supply power. Regulated " +
      "charges are billed on the energy absorbed from the grid, including " +
      "energy used to charge the battery.",
    legalBasis:
      "ΚΥΑ ΥΠΕΝ/ΔΑΠΕΕΚ/102876/1958/2026 (ΦΕΚ Β' 5573/2026), άρθρα 2 §7, " +
      "4 §4β, 8 §1α–§3, 10, 12α §3, 13.",
  },
};

const ZERO_FEED_IN_GENERIC_DEFAULTS = {
  documented: false,
  countryLabel: null,
  schemeName: "Zero feed-in (self-consumption without export)",
  gridChargingPermitted: true,
  note:
    "No documented national zero feed-in framework is bundled for this " +
    "country. No export is modelled either way, but the connection terms " +
    "(sizing limits, grid charging, contract position) are user-supplied " +
    "assumptions and must be verified against the applicable regulation.",
  legalBasis: null,
};

export function zeroFeedInCountryDefaults(country) {
  const key = String(country || "").trim().toUpperCase();
  const doc = ZERO_FEED_IN_COUNTRY_DEFAULTS[key];
  if (doc) return { ...doc };
  return { ...ZERO_FEED_IN_GENERIC_DEFAULTS, countryLabel: key || null };
}

export function isZeroFeedIn(btm) {
  return String(btm?.gridInjection?.mode || "allowed") === "zero_feed_in";
}

export function isPvOnlyExport(btm) {
  return String(btm?.gridInjection?.mode || "allowed") === "pv_only";
}

// Documented per country, as for zero feed-in. Greece: storage on its own
// inverter and meter that may not inject is one of the cases in which the
// battery may absorb from the grid (interconnected system only).
export const PV_ONLY_EXPORT_COUNTRY_DEFAULTS = {
  GR: {
    documented: true,
    countryLabel: "Greece",
    schemeName: "Έγχυση μόνο από Φ/Β — αποθήκευση χωρίς έγχυση",
    gridChargingPermitted: true,
    note:
      "Greek self-consumption with storage that does not inject: the PV " +
      "surplus is exported and settled under the selected scheme, while the " +
      "storage unit — on its own inverter and its own meter, barred from " +
      "injecting — serves the site's supply only. That arrangement is one of " +
      "the cases in which the battery may absorb from the grid on the " +
      "interconnected system; on a non-interconnected island the storage may " +
      "neither absorb from nor inject into the island grid. Regulated charges " +
      "are billed on the energy absorbed from the grid, including energy used " +
      "to charge the battery.",
    legalBasis:
      "ΚΥΑ ΥΠΕΝ/ΔΑΠΕΕΚ/102876/1958/2026 (ΦΕΚ Β' 5573/2026), άρθρο 8 §1β, " +
      "§2, §3, άρθρο 10.",
  },
};

const PV_ONLY_EXPORT_GENERIC_DEFAULTS = {
  documented: false,
  countryLabel: null,
  schemeName: "Export from PV only (storage that does not inject)",
  gridChargingPermitted: true,
  note:
    "No documented national framework for storage that does not inject is " +
    "bundled for this country. The modelled behaviour — PV surplus exported, " +
    "battery discharging into the site load only — is the same everywhere, " +
    "but the connection terms (separate metering, whether the battery may " +
    "charge from the grid, contract position) are user-supplied assumptions " +
    "and must be verified against the applicable regulation.",
  legalBasis: null,
};

export function pvOnlyExportCountryDefaults(country) {
  const key = String(country || "").trim().toUpperCase();
  const doc = PV_ONLY_EXPORT_COUNTRY_DEFAULTS[key];
  if (doc) return { ...doc };
  return { ...PV_ONLY_EXPORT_GENERIC_DEFAULTS, countryLabel: key || null };
}

// ---------------------------------------------------------------------------
// Switches the Behind-the-Meter selections imply.
//
// Under BTM the BESS page's grid switches are not free choices: each one
// follows from the value streams and the grid injection regime on the General
// page. They are set automatically, locked, and explained under the switch.
// One function per switch, so the General page (which writes the value), the
// BESS page (which shows it) and the run payload (which sends it) cannot give
// three different answers. The backend applies the same rules (read_config).
// ---------------------------------------------------------------------------

export function isStandaloneBessSystem(cfg) {
  const type = String(cfg?.systemType || "").trim().toLowerCase();
  return type === "standalone bess" || (type.includes("standalone") && type.includes("bess"));
}

// "Enable Grid Charging"
export const BTM_GRID_CHARGING_HINT =
  "Controlled by the Behind-the-Meter value streams on the General page: " +
  "enabling 'Retail time-of-use arbitrage' allows off-peak grid charging; with " +
  "it off, the battery charges from surplus generation only.";
export const BTM_GRID_CHARGING_TOU_CAPTION =
  "Behind-the-Meter: off-peak grid charging enabled for retail time-of-use arbitrage.";
export const BTM_GRID_CHARGING_SURPLUS_CAPTION =
  "Behind-the-Meter: surplus-only charging enforced.";
export const BTM_GRID_CHARGING_STANDALONE_HINT =
  "Locked ON: a standalone battery behind the meter has no other energy " +
  "source, so it always charges from the grid, and retail time-of-use " +
  "arbitrage is always part of its savings.";
export const BTM_GRID_CHARGING_STANDALONE_CAPTION =
  "Behind-the-Meter: standalone battery, grid charging always on.";
// Outside BTM a standalone battery is locked ON for the same physical reason.
export const STANDALONE_GRID_CHARGING_HINT =
  "Locked ON: a standalone battery has no other energy source, so it charges " +
  "from the grid.";
export const STANDALONE_GRID_CHARGING_CAPTION = "Standalone battery: grid charging always on.";

// Greece, ΚΥΑ ΥΠΕΝ/ΔΑΠΕΕΚ/102876/1958/2026 art. 8 par. 1: storage in a
// self-consumption installation may absorb from the grid only under zero
// feed-in, or on its own inverter and meter with no injection (or for an
// EV). Under "export permitted" the battery injects, so retail time-of-use
// arbitrage (grid charging by definition) is locked OFF with the reason. The
// user's stored choice is kept, so switching the regime restores it; the
// payload and the backend (read_config) force it off, as for zero feed-in.
export const TOU_GR_INJECTING_LOCKED_TEXT =
  "Locked OFF: under the Greek self-consumption framework a battery that may " +
  "inject into the grid may not charge from it (ΚΥΑ ΥΠΕΝ/ΔΑΠΕΕΚ/102876/1958/2026, " +
  "art. 8 par. 1). Grid charging is available under zero feed-in, or with " +
  "'Export from PV only' (storage on its own inverter and meter that does not inject).";
export const TOU_GR_INJECTING_RUN_NOTE =
  "Retail time-of-use arbitrage will be switched OFF for this run: in Greece a " +
  "battery that may inject into the grid may not charge from it. Select " +
  "'Export from PV only' or zero feed-in to allow grid charging.";
export const BTM_GRID_CHARGING_GR_INJECTING_CAPTION =
  "Behind-the-Meter (Greece): a battery that may inject does not charge from the grid.";

function btmCountry(btm, cfg, country) {
  return String(
    country || btm?.gridInjection?.country || btm?.netBilling?.country || cfg?.biddingZone || ""
  ).trim().toUpperCase();
}

/** True when the Greek framework rules grid charging out for this battery. */
export function touArbitrageBlockedByFramework(btm, cfg, { standalone, country } = {}) {
  const isStandalone = standalone === undefined ? isStandaloneBessSystem(cfg) : Boolean(standalone);
  if (isStandalone) return false;
  const mode = String(btm?.gridInjection?.mode || "allowed");
  if (mode !== "allowed") return false;
  return btmCountry(btm, cfg, country).startsWith("GR");
}

export function btmGridChargingState(btm, cfg) {
  if (isStandaloneBessSystem(cfg)) {
    return {
      on: true,
      hint: BTM_GRID_CHARGING_STANDALONE_HINT,
      caption: BTM_GRID_CHARGING_STANDALONE_CAPTION,
    };
  }
  if (isVirtualNetBilling(btm)) {
    return { on: false, hint: VNB_GRID_CHARGING_HINT, caption: VNB_GRID_CHARGING_CAPTION };
  }
  if (touArbitrageBlockedByFramework(btm, cfg)) {
    return {
      on: false,
      hint: TOU_GR_INJECTING_LOCKED_TEXT,
      caption: BTM_GRID_CHARGING_GR_INJECTING_CAPTION,
    };
  }
  const tou = Boolean(btm?.touArbitrage?.enabled);
  return {
    on: tou,
    hint: BTM_GRID_CHARGING_HINT,
    caption: tou ? BTM_GRID_CHARGING_TOU_CAPTION : BTM_GRID_CHARGING_SURPLUS_CAPTION,
  };
}

// "Grid injection at the connection point (PV + battery)"
export const BTM_INJECTION_ZERO_FEED_IN_HINT =
  "Locked OFF by the Behind-the-Meter zero feed-in regime on the General page: " +
  "the station never exports. Change the grid injection regime there to re-enable.";
export const BTM_INJECTION_ZERO_FEED_IN_CAPTION =
  "Behind-the-Meter: zero feed-in, no export enforced.";
export const BTM_INJECTION_PV_ONLY_HINT =
  "Locked ON by the Behind-the-Meter 'export from PV only' regime on the " +
  "General page: this switch is the connection point's, and it must be ON for " +
  "the PV surplus to leave the site. The battery is kept off the grid by its " +
  "own line below, which stays OFF.";
export const BTM_INJECTION_PV_ONLY_CAPTION =
  "Behind-the-Meter: the connection point exports PV surplus only.";
export const BTM_INJECTION_ALLOWED_HINT =
  "Locked ON by the Behind-the-Meter settings on the General page: export is " +
  "permitted and the surplus is valued (fixed feed-in or net billing), so PV " +
  "surplus and battery discharge may be exported, up to the export limit.";
export const BTM_INJECTION_ALLOWED_CAPTION =
  "Behind-the-Meter: export permitted and valued.";
export const BTM_INJECTION_UNPRICED_HINT =
  "Locked OFF by the Behind-the-Meter settings on the General page: the surplus " +
  "has no value (curtailed, net billing off), so nothing is exported. Set a " +
  "fixed feed-in price or enable net billing there to export.";
export const BTM_INJECTION_UNPRICED_CAPTION =
  "Behind-the-Meter: the surplus has no value, nothing is exported.";
export const BTM_INJECTION_STANDALONE_HINT =
  "Locked OFF: a standalone battery behind the meter serves the site load only; " +
  "with no generation there is no surplus to export.";
export const BTM_INJECTION_STANDALONE_CAPTION =
  "Behind-the-Meter: standalone battery, nothing is exported.";

export function btmGridInjectionState(btm, cfg) {
  const mode = String(btm?.gridInjection?.mode || "allowed");
  if (mode === "zero_feed_in") {
    return { on: false, reason: "zero_feed_in", hint: BTM_INJECTION_ZERO_FEED_IN_HINT, caption: BTM_INJECTION_ZERO_FEED_IN_CAPTION };
  }
  if (isStandaloneBessSystem(cfg)) {
    return { on: false, reason: "standalone", hint: BTM_INJECTION_STANDALONE_HINT, caption: BTM_INJECTION_STANDALONE_CAPTION };
  }
  // The station injects all of its output, whether the surplus is paid or not.
  if (mode === VNB_MODE) {
    return { on: true, reason: VNB_MODE, hint: VNB_INJECTION_HINT, caption: VNB_INJECTION_CAPTION };
  }
  const priced = btm?.surplus?.treatment === "fixed_price" || Boolean(btm?.netBilling?.enabled);
  if (!priced) {
    return { on: false, reason: "unpriced", hint: BTM_INJECTION_UNPRICED_HINT, caption: BTM_INJECTION_UNPRICED_CAPTION };
  }
  if (mode === "pv_only") {
    return { on: true, reason: "pv_only", hint: BTM_INJECTION_PV_ONLY_HINT, caption: BTM_INJECTION_PV_ONLY_CAPTION };
  }
  return { on: true, reason: "allowed", hint: BTM_INJECTION_ALLOWED_HINT, caption: BTM_INJECTION_ALLOWED_CAPTION };
}

// "Simultaneous injecting & drawing": one meter behind the meter, so in any
// hour the site either imports or exports. The study always models it so.
export const BTM_INJECT_DRAW_HINT =
  "Locked OFF behind the meter: the site has one meter, so in each hour it " +
  "either imports or exports. The study always models it that way.";
export const BTM_INJECT_DRAW_CAPTION = "Behind-the-Meter: import or export, never both in the same hour.";

// "Battery injection": the battery's own share of the connection point.
// Behind the meter it is never a free choice either: ON only when export is
// permitted from the whole station, OFF under "export from PV only" (the
// dispatch caps discharge at the load still unserved after PV) and whenever
// the connection point itself exports nothing.
export const BTM_BATTERY_INJECTION_ALLOWED_HINT =
  "Locked ON by 'export permitted' on the General page: battery discharge may " +
  "also be exported when that pays (at the feed-in price or under net " +
  "billing), up to the export limit.";
export const BTM_BATTERY_INJECTION_ALLOWED_CAPTION =
  "Behind-the-Meter: the battery may export as well as the PV.";
export const BTM_BATTERY_INJECTION_PV_ONLY_HINT =
  "Locked OFF by 'export from PV only' on the General page: the battery serves " +
  "the site load only. In every hour the dispatch caps its discharge at the load " +
  "still unserved after PV, so none of its energy reaches the meter.";
export const BTM_BATTERY_INJECTION_PV_ONLY_CAPTION =
  "Behind-the-Meter: the battery never injects; it discharges into the site load only.";
export const BTM_BATTERY_INJECTION_NONE_HINT =
  "Locked OFF: the connection point exports nothing with these settings (see " +
  "the switch above), so neither does the battery.";
export const BTM_BATTERY_INJECTION_NONE_CAPTION =
  "Behind-the-Meter: nothing is exported, battery included.";

export function btmBatteryInjectionState(btm, cfg) {
  const station = btmGridInjectionState(btm, cfg);
  if (!station.on) {
    return { on: false, hint: BTM_BATTERY_INJECTION_NONE_HINT, caption: BTM_BATTERY_INJECTION_NONE_CAPTION };
  }
  if (station.reason === "pv_only") {
    return { on: false, hint: BTM_BATTERY_INJECTION_PV_ONLY_HINT, caption: BTM_BATTERY_INJECTION_PV_ONLY_CAPTION };
  }
  if (station.reason === VNB_MODE) {
    return { on: true, hint: VNB_BATTERY_INJECTION_HINT, caption: VNB_BATTERY_INJECTION_CAPTION };
  }
  return { on: true, hint: BTM_BATTERY_INJECTION_ALLOWED_HINT, caption: BTM_BATTERY_INJECTION_ALLOWED_CAPTION };
}

// "Simultaneous PV + Grid Charging" is a free modelling choice while the
// battery may charge from the grid; with no grid charging it has nothing to
// combine and is cleared.
export function btmSimultaneousGridPvState(btm, cfg, stored) {
  const charging = btmGridChargingState(btm, cfg).on;
  const hasPv = !isStandaloneBessSystem(cfg);
  return { visible: charging && hasPv, value: charging && hasPv ? Boolean(stored) : false };
}

/**
 * The config with every BESS-page switch that BTM implies set to its implied
 * value. The General page commits this on every BTM change, so the BESS page
 * and the saved project always show what the run will use.
 */
export function alignBtmSwitches(cfg) {
  const btm = normalizeBtm(cfg);
  return {
    ...cfg,
    allowGridCharging: btmGridChargingState(btm, cfg).on,
    allowGridInjection: btmGridInjectionState(btm, cfg).on,
    simultaneousInjectDraw: false,
    simultaneousGridPv: btmSimultaneousGridPvState(btm, cfg, cfg?.simultaneousGridPv).value,
  };
}

// The run notes: what the run will switch because the stored BESS-page value
// is not the implied one (an older project, or a system-type change).
export const BTM_INJECTION_ON_RUN_NOTE =
  "'Grid injection at the connection point' on the BESS page will be switched " +
  "ON for this run: export is permitted and the surplus is valued on this page.";
export const BTM_INJECTION_OFF_RUN_NOTE =
  "'Grid injection at the connection point' on the BESS page will be switched " +
  "OFF for this run: with these settings nothing is exported.";

// ---------------------------------------------------------------------------
// Net billing (καθαρή χρέωση / συμψηφισμός). Mirrors the backend btm.py
// net-billing enums. Distinct from net METERING (1:1 kWh rollover): injected
// surplus is valued in MONEY (market or fixed) and netted against consumption
// within a settlement period. It is the scheme a licensed aggregator (in
// Greece, a ΦΟΣΕ such as GreenValue) signs on behalf of the prosumer.
// ---------------------------------------------------------------------------

export const NET_BILLING_HELPER_TEXT =
  "Instead of curtailing or feeding-in surplus at a flat price, value the " +
  "injected surplus in money — at the day-ahead market price or a fixed agreed " +
  "price — and net that credit against the cost of the energy still drawn from " +
  "the grid. This replaces the fixed feed-in above (they are two treatments of " +
  "the same injected energy). Net metering with carried-over kWh credits is a " +
  "different scheme and is not modelled.";

export const NET_BILLING_INJECTION_BASIS_OPTIONS = [
  {
    value: "dam_market",
    label: "Day-ahead market price (as settled by the aggregator)",
    description:
      "Injected surplus is credited at the hourly day-ahead wholesale price, " +
      "the way a licensed aggregator settles it through the market operator. " +
      "The credit tracks the market hour by hour and carries market price " +
      "risk, exactly like a merchant sell leg.",
  },
  {
    value: "fixed_agreed",
    label: "Fixed agreed price with the aggregator (EUR/MWh)",
    description:
      "Injected surplus is credited at a single fixed price per MWh agreed " +
      "with the aggregator for the whole horizon (the third-party investment " +
      "model quotes around 70-80 EUR/MWh). Price-certain and independent of " +
      "the wholesale market.",
  },
];

export const NET_BILLING_SETTLEMENT_PERIOD_OPTIONS = [
  {
    value: "monthly",
    label: "Netted each calendar month",
    description:
      "The money value of injected surplus offsets the energy cost of grid " +
      "consumption within the same calendar month. A credit above the month's " +
      "consumption goes to the surplus-credit treatment; it does not roll into " +
      "the next month's energy netting.",
  },
  {
    value: "annual",
    label: "Netted once over the year",
    description:
      "Injection credit and consumption cost are netted across the whole year. " +
      "Annual netting is more favourable than monthly — a summer export surplus " +
      "can offset a winter consumption deficit — and must match what the " +
      "contract actually allows.",
  },
];

export const NET_BILLING_SURPLUS_CREDIT_OPTIONS = [
  {
    value: "paid_out",
    label: "Paid out at the injection price",
    description:
      "When injection credit exceeds consumption cost in a settlement period, " +
      "the excess is paid to the project at the same injection price. The " +
      "project is then a net exporter for value, not only a self-consumer.",
  },
  {
    value: "expires",
    label: "Expires with no payment",
    description:
      "Injection credit above the period's consumption cost is lost. This caps " +
      "the value of oversizing generation against the site load and is the " +
      "conservative assumption where the contract does not pay for net export.",
  },
];

// Who captures the injected-surplus revenue. Attribution lens only — it never
// changes the modelled totals, only who is shown as receiving them. Mirrors
// NET_BILLING_INJECTION_RECIPIENT_* in the backend btm.py.
export const NET_BILLING_INJECTION_RECIPIENT_OPTIONS = [
  {
    value: "host",
    label: "Host / site owner",
    description:
      "The site owner captures every value stream: the on-site self-consumption " +
      "and peak-shaving savings AND the injected-surplus credit. Default view.",
  },
  {
    value: "third_party",
    label: "Third-party investor / aggregator (ΦΟΣΕ)",
    description:
      "A third party funds the system and receives the injected-surplus revenue, " +
      "while the host keeps the on-site self-consumption and peak-shaving " +
      "savings. The report attributes each modelled stream to the party that " +
      "receives it; it does not change the modelled totals.",
  },
];

// Per-country documented defaults. Greece is populated from the live framework;
// every other country resolves to a generic set the UI flags as an assumption
// to verify — mirrors NET_BILLING_COUNTRY_DEFAULTS in the backend btm.py.
export const NET_BILLING_COUNTRY_DEFAULTS = {
  GR: {
    documented: true,
    countryLabel: "Greece",
    injectionPriceBasis: "dam_market",
    settlementPeriod: "monthly",
    surplusCreditTreatment: "paid_out",
    counterpartyRole: "Licensed aggregator (ΦΟΣΕ)",
    note:
      "Greek net billing: self-consumed energy avoids the retail tariff; " +
      "injected surplus is valued at the day-ahead market price and settled " +
      "through a licensed aggregator (ΦΟΣΕ). The scheme is in force but its " +
      "clearing processes (ΔΑΠΕΕΠ / ΔΕΔΔΗΕ / supplier / aggregator) are still " +
      "being finalised — figures are indicative of the scheme as written.",
  },
};

const NET_BILLING_GENERIC_DEFAULTS = {
  documented: false,
  countryLabel: null,
  injectionPriceBasis: "fixed_agreed",
  settlementPeriod: "monthly",
  surplusCreditTreatment: "expires",
  counterpartyRole: "Aggregator / offtaker for injected energy",
  note:
    "No documented national net-billing framework is bundled for this country. " +
    "The parameters below are user-supplied assumptions and must be verified " +
    "against the applicable regulation before the figures are relied upon.",
};

// What a behind-the-meter run values its energy at, for the simulation page's
// run-scope card. A BTM study is not priced on any wholesale market: the
// saving is valued at the retail tariff, and only the surplus, when it has a
// value, can carry a market price. The card printed "Markets priced: Day-ahead"
// on a workshop's savings study. One label per entry, so each one is a whole
// text node the translation bridge can replace.
export function btmValuationLabels(btm, cfg) {
  const labels = ["Retail tariff"];
  if (isZeroFeedIn(btm)) {
    labels.push("Zero feed-in (no export)");
    return labels;
  }
  if (btm?.netBilling?.enabled) {
    const country = btm.netBilling.country || cfg?.biddingZone || "";
    const basis = btm.netBilling.injectionPriceBasis || netBillingCountryDefaults(country).injectionPriceBasis;
    labels.push(basis === "fixed_agreed"
      ? "Net-billing credit at a fixed agreed price"
      : "Net-billing credit at the day-ahead price");
  } else if (String(btm?.surplus?.treatment || "none") === "fixed_price") {
    labels.push(isUnpaidSurplusExport(btm) ? "Surplus export without compensation" : "Surplus at a fixed feed-in price");
  }
  if (isPvOnlyExport(btm)) labels.push("Battery does not inject");
  return labels;
}

export function netBillingCountryDefaults(country) {
  const key = String(country || "").trim().toUpperCase();
  const doc = NET_BILLING_COUNTRY_DEFAULTS[key];
  if (doc) return { ...doc };
  return { ...NET_BILLING_GENERIC_DEFAULTS, countryLabel: key || null };
}

export const ESCALATION_HELPER_TEXT =
  "Annual increase in the retail electricity price, applied to the savings " +
  "projection over the assessment period. This is a commercial assumption, " +
  "not a forecast, and it is the single input the cumulative saving is most " +
  "sensitive to — check it against the site's supply contract history.";

export const ENERGY_SHARE_HELPER_TEXT =
  "The share of the variable retail price per kWh that is the energy (supply) charge, " +
  "from the site's bill: supply charge ÷ all per-kWh charges. The supplier buys that " +
  "energy on the wholesale market, so with a forward price curve on the Financial Assumptions page " +
  "this part follows the price path; the rest (network charges, public-service charges " +
  "and levies) rises at the escalation of the regulated charges. Blank = one escalation " +
  "for the whole price, as before.";

export const REGULATED_ESCALATION_LABEL = "Escalation of the regulated charges (%/year)";

export const REGULATED_ESCALATION_HELPER_TEXT =
  "Network charges, public-service charges and levies: they are set by the regulator, not by " +
  "the market. Blank = the study's inflation on the Financial Assumptions page:";

export const ESCALATION_NOT_USED_TEXT =
  "Not used while the retail price is split into an energy part and a regulated part (below).";

export const RETAIL_SPLIT_ON_TEXT =
  "The energy part follows the forward price path set on the Financial Assumptions page; the regulated " +
  "part rises at the escalation of the regulated charges. The whole-price escalation above " +
  "is not used.";

export const RETAIL_SPLIT_NO_PATH_TEXT =
  "The study has no forward price path, so the energy share is not used and the whole price " +
  "follows the retail escalation above. To use it, choose \"Forward price curve (re-dispatch)\" " +
  "under Financial Assumptions → Merchant Revenue Projection; \"Load MetEn house path (live anchors)\" fills the path in " +
  "one click.";

export const REGULATED_ESCALATION_INVALID_TEXT =
  "The escalation of the regulated charges must be a number (percent per year).";

// Whether the study splits the retail price: an energy share AND a forward
// price path (backend btm_pv_only.retail_price_factors).
export function retailSplitState(btm, cfg) {
  const raw = btm?.retailTariff?.energySharePct;
  const shareSet = raw !== "" && raw !== null && raw !== undefined && Number.isFinite(Number(raw));
  const forwardCurve = cfg?.merchantRevenueProjection?.mode === "forward_curve_redispatch";
  const inflation = cfg?.feasibility?.economics?.financialParams?.inflationRatePctYear;
  // A dynamic tariff always splits the price: the energy part follows the
  // study's day-ahead prices and its share comes from the dispatch, so no share
  // is entered (backend btm_pv_only._dynamic_retail_factors).
  const dynamic = dynamicTariffState(cfg).dynamic;
  return {
    shareSet,
    forwardCurve,
    dynamic,
    active: dynamic || (shareSet && forwardCurve),
    inflationPct: inflation === "" || inflation === null || inflation === undefined ? "" : String(inflation),
  };
}

export const ENERGY_SHARE_INVALID_TEXT = "The energy share must be a number from 0 to 100 (%).";

export const DYNAMIC_TARIFF_INVALID_TEXT =
  "The dynamic tariff's supplier fee and regulated charges must be numbers of 0 or more (EUR/kWh). " +
  "Correct them on the Feasibility > Technical page under Demand & Tariffs.";

export const DEFAULT_BTM = {
  retailTariff: {
    // Annual escalation of the retail price (%/year). Energy prices themselves
    // come from the project's monthly peak / non-peak tariff table.
    escalationPctYear: "",
    // Share (%) of the per-kWh retail price that is energy; blank = one
    // escalation for the whole price (backend btm_pv_only.retail_price_factors).
    energySharePct: "",
    // Escalation (%/year) of the regulated part when the price is split;
    // blank = the study's inflation.
    regulatedEscalationPctYear: "",
  },
  demandCharge: {
    enabled: false,
    eurPerKwMonth: "",
    eurPerKwYear: "",
    measurementPeriod: "monthly", // monthly | annual
    measurementInterval: "hourly", // hourly | 15min
    basis: "monthly_peak", // monthly_peak | peak_window
  },
  touArbitrage: { enabled: false },
  // Battery wear priced in the dispatch (see WEAR_COST_MODE_OPTIONS).
  wearCost: {
    mode: "auto", // auto | custom | off
    eurPerMwh: "", // custom only
  },
  gridInjection: {
    mode: "allowed", // allowed | zero_feed_in
  },
  surplus: {
    treatment: "none", // none | fixed_price
    feedInEurPerMwh: "",
  },
  netBilling: {
    enabled: false,
    country: "", // blank -> backend uses the project bidding zone
    injectionPriceBasis: "", // blank -> country default (dam_market | fixed_agreed)
    fixedInjectionPriceEurPerMwh: "",
    settlementPeriod: "", // blank -> country default (monthly | annual)
    surplusCreditTreatment: "", // blank -> country default (paid_out | expires)
    injectionRevenueRecipient: "host", // host | third_party (attribution lens)
    thirdPartyInvestorPriceEurPerMwh: "", // optional investor recovery basis
  },
};

/**
 * True when a metered profile is in use and the engine will actually consume it,
 * in which case the synthesised-profile inputs (monthly consumption, workday
 * schedule) are reference data the engine never reads.
 *
 * The counts mirror Configuration/load_profile.py:detect_resolution EXACTLY.
 * Counting any non-empty array was wrong in the strict direction: a partial file
 * silenced the checks here while the backend, which ignores a profile it cannot
 * align to a full year, still raised on the missing monthly figures.
 */
const FULL_YEAR_PROFILE_COUNTS = new Set([8760, 8784, 35040, 35136]);

export function loadProfileActive(cfg) {
  const profile = cfg?.feasibility?.technical?.demand?.loadProfile;
  // Stored and chosen are two different things. A file the user has switched
  // away from is still on the page to inspect, but it is not the load.
  if (profile?.use === false) return false;
  const values = profile?.values;
  if (!Array.isArray(values) || !FULL_YEAR_PROFILE_COUNTS.has(values.length)) return false;
  const declared = String(profile.resolution || "").trim().toLowerCase();
  const inferred = values.length > 8784 ? "15min" : "hourly";
  const resolution = ["hourly", "15min"].includes(declared) ? declared : inferred;
  const hours = resolution === "15min" ? values.length / 4 : values.length;
  if (![8760, 8784].includes(hours)) return false;
  // The backend falls back to generated demand for an empty-energy profile.
  // Keep its required demand inputs accessible in that case.
  return values.some((value) => {
    const number = Number(typeof value === "string" ? value.trim().replace(",", ".") : value);
    return Number.isFinite(number) && number > 0;
  });
}

export function isBtmSettlement(settlementType) {
  return String(settlementType || "").trim().toUpperCase() === BTM_SETTLEMENT_VALUE;
}

export function normalizeBtm(cfg) {
  const raw =
    (cfg && cfg.btm) ||
    (cfg && cfg.feasibility && cfg.feasibility.technical && cfg.feasibility.technical.btm) ||
    {};
  const basis = DEMAND_CHARGE_BASIS_OPTIONS.some((o) => o.value === raw.demandCharge?.basis)
    ? raw.demandCharge.basis
    : DEFAULT_BTM.demandCharge.basis;
  const treatment = SURPLUS_TREATMENT_OPTIONS.some((o) => o.value === raw.surplus?.treatment)
    ? raw.surplus.treatment
    : DEFAULT_BTM.surplus.treatment;
  // Accepts {mode} or a bare string, as the backend does; anything unknown is
  // the default (export permitted), which is what every saved project has.
  const rawMode =
    typeof raw.gridInjection === "string" ? raw.gridInjection : raw.gridInjection?.mode;
  const mode = GRID_INJECTION_OPTIONS.some((o) => o.value === rawMode)
    ? rawMode
    : DEFAULT_BTM.gridInjection.mode;
  const wearMode = WEAR_COST_MODE_OPTIONS.some((o) => o.value === raw.wearCost?.mode)
    ? raw.wearCost.mode
    : DEFAULT_BTM.wearCost.mode;
  return {
    ...DEFAULT_BTM,
    ...raw,
    retailTariff: { ...DEFAULT_BTM.retailTariff, ...(raw.retailTariff || {}) },
    demandCharge: { ...DEFAULT_BTM.demandCharge, ...(raw.demandCharge || {}), basis },
    touArbitrage: { ...DEFAULT_BTM.touArbitrage, ...(raw.touArbitrage || {}),
      ...(String(cfg?.systemType || "").trim().toLowerCase() === "standalone pv" ? { enabled: false } : {}) },
    wearCost: { ...DEFAULT_BTM.wearCost, ...(raw.wearCost || {}), mode:
      String(cfg?.systemType || "").trim().toLowerCase() === "standalone pv" ? "off" : wearMode },
    gridInjection: {
      ...DEFAULT_BTM.gridInjection,
      ...(typeof raw.gridInjection === "object" && raw.gridInjection ? raw.gridInjection : {}),
      mode,
    },
    // The user's surplus and net-billing settings are KEPT in the config so
    // switching zero feed-in off restores them; the payload builder and the
    // backend are what force them off while the regime is on.
    surplus: { ...DEFAULT_BTM.surplus, ...(raw.surplus || {}), treatment },
    netBilling: { ...DEFAULT_BTM.netBilling, ...(raw.netBilling || {}) },
  };
}

/**
 * Validate a normalized BTM block against the rest of the project config.
 * Returns a { fieldKey: message } map; empty means valid.
 *
 * The backend raises on the same conditions (read_config), so these are the
 * errors that would otherwise surface only after a run is queued.
 */
// A month left empty among months that carry a load is almost always a figure
// not typed in: a workshop study ran with December blank, 11 months of load
// and nothing said. Mirrors the backend warning (read_config), same wording.
export const CONSUMPTION_MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function emptyMonthWarning(month) {
  return (
    `No consumption was entered for ${month} while the other months carry a load, ` +
    "so the modelled year has no load in that month and no saving is counted for it. " +
    "Enter the month's consumption from the bills, or confirm that the site is closed that month."
  );
}

/** Months with no consumption while others carry a load (monthly input only). */
export function emptyConsumptionMonths(cfg) {
  const demand = cfg?.feasibility?.technical?.demand || {};
  if (demand.enabled === false || loadProfileActive(cfg)) return [];
  const type = String(demand.inputType || "Monthly Consumption (kWh)");
  if (!type.startsWith("Monthly")) return [];
  const monthly = demand.monthly || {};
  const value = (name) => {
    const raw = monthly[name] ?? monthly[name.slice(0, 3)];
    const number = Number(typeof raw === "string" ? raw.trim().replace(",", ".") : raw);
    return Number.isFinite(number) ? number : 0;
  };
  const values = CONSUMPTION_MONTH_NAMES.map(value);
  if (!values.some((v) => v > 0)) return [];
  return CONSUMPTION_MONTH_NAMES.filter((name, i) => !(values[i] > 0));
}

// What the run is missing, in the terms of the consumption entry the page
// shows. "monthly" keeps the original message, word for word.
export function consumptionEntryPeriod(inputType = "Monthly Consumption (kWh)") {
  const type = String(inputType || "");
  if (type.startsWith("Annual")) return "annual";
  if (/^(Quarterly|Tri-Monthly)/.test(type)) return "quarterly";
  if (type.startsWith("Bi-Monthly")) return "bimonthly";
  return "monthly";
}
export const CONSUMPTION_MISSING_TEXT = {
  monthly:
    "Enter the site's monthly consumption (kWh) on the " +
    "Feasibility > Technical page — every month is currently zero or empty. " +
    "Importing a metered load profile removes the need for these figures.",
  bimonthly:
    "Enter the site's consumption for each two-month period (kWh) on the " +
    "Feasibility > Technical Assumptions page, under Demand. Importing a " +
    "metered load profile removes the need for these figures.",
  quarterly:
    "Enter the site's consumption for each quarter (kWh) on the " +
    "Feasibility > Technical Assumptions page, under Demand. Importing a " +
    "metered load profile removes the need for these figures.",
  annual:
    "Enter the site's annual consumption (kWh) on the " +
    "Feasibility > Technical Assumptions page, under Demand. Importing a " +
    "metered load profile removes the need for this figure.",
};
export const SINGLE_PRICE_MISSING_TEXT =
  "Enter the electricity price (EUR/kWh) on the Feasibility > Technical " +
  "Assumptions page, under Tariffs. With 'Same price for every month' one " +
  "figure is enough.";

export function validateBtm(btm, cfg) {
  const errors = {};
  if (!btm || !isBtmSettlement(cfg?.settlementType)) return errors;

  const technical = cfg?.feasibility?.technical || {};
  const demand = technical.demand || {};
  const tariffs = technical.tariffs || {};
  const isStandaloneBess = isStandaloneBessSystem(cfg);

  // A BTM project is defined by its load.
  if (demand.enabled === false) {
    errors.demand =
      "Behind-the-Meter measures savings against the site's own consumption. " +
      "Enable 'Include Demand' on the Feasibility > Technical page and enter " +
      "the site's monthly consumption, or import a metered load profile.";
  } else if (!loadProfileActive(cfg)) {
    // Only meaningful for a SYNTHESISED profile. With a metered one imported the
    // monthly figures are reference data the engine never reads -- read_config
    // gates the identical check on `not load_profile_enabled` for that reason -
    // so requiring them here blocked EXECUTE STUDY on a config the engine
    // accepts, and asked the user to fill in fields it was about to ignore.
    const monthly = demand.monthly || {};
    const hasConsumption = Object.values(monthly).some((v) => Number(v) > 0);
    if (!hasConsumption) {
      // Named the way the page asks for it: one annual figure, or one per
      // two months / quarter, reads differently from twelve monthly ones.
      errors.demand = CONSUMPTION_MISSING_TEXT[consumptionEntryPeriod(demand.inputType)];
    }
  }

  // Savings are priced at the retail tariff, so all twelve months are needed -
  // unless the tariff is dynamic: every hour is then priced at the day-ahead
  // price + fee + regulated charges, and the table is not used.
  const dynamicTariff = isDynamicScheme(tariffs.billingScheme);
  if (dynamicTariff && Object.keys(validateDynamicTariff(tariffs)).length) {
    errors.tariffs = DYNAMIC_TARIFF_INVALID_TEXT;
  }
  const itemizedTariff = !dynamicTariff && tariffs.rateMode === ITEMIZED_TARIFF_MODE;
  if (!dynamicTariff && !itemizedTariff && legacyFixedChargeErrors(tariffs).length) {
    errors.tariffs = "Fixed charges for the single effective price must be non-negative monthly amounts. Blank means zero.";
  }
  if (itemizedTariff && itemizedTariffErrors(tariffs, CONSUMPTION_MONTH_NAMES).length) {
    errors.tariffs = "Complete the energy charge for all twelve months, other per-kWh charges and electricity VAT. For peak/off-peak pricing, complete both rates. PSO and optional fixed charges must be non-negative.";
  }
  const monthlyTariffs = tariffs.monthly || {};
  const monthsWithBothRates = Object.values(monthlyTariffs).filter(
    (m) => Number(m?.peak) > 0 && Number(m?.offPeak) > 0
  );
  if (!dynamicTariff && !itemizedTariff && monthsWithBothRates.length < 12) {
    // One rate all day asks for one price per month (or one for the year with
    // "Same price for every month"), not a peak and a non-peak rate.
    errors.tariffs = tariffs.legacyPricingMode === SINGLE_ENERGY_RATE
      ? SINGLE_PRICE_MISSING_TEXT
      : "Behind-the-Meter prices every saving at the retail tariff, so all " +
        "twelve months need a peak and a non-peak rate (EUR/kWh). Complete them " +
        "on the Feasibility > Technical page under Demand & Tariffs.";
  }

  // Operating hours. The schedule selects display a default they do not persist,
  // so a config can easily arrive with every day empty — which generates a year
  // of zero load and silently produces zero savings. Only meaningful for
  // Commercial; residential profiles are modelled around the clock.
  const isCommercial =
    String(cfg?.useCase || "Commercial").trim().toLowerCase() === "commercial";
  const scheduleRequired = demand.shape?.version === 1 ? demand.shape.useSchedule : isCommercial;
  if (scheduleRequired && !loadProfileActive(cfg)) {
    const schedule = demand.schedule || {};
    const dayIsOpen = (day) => {
      const block = schedule[day] || {};
      const start = String(block.startHour ?? "").trim();
      const end = String(block.endHour ?? "").trim();
      if (!start || !end || start.toLowerCase() === "none" || end.toLowerCase() === "none") {
        return false;
      }
      return start !== end;
    };
    if (!["weekdays", "saturdays", "sundays"].some(dayIsOpen)) {
      errors.schedule =
        "Every day type in the Workday Schedule is currently closed, so the " +
        "modelled consumption would be zero for every hour of the year. Set " +
        "operating hours for at least one day type on the " +
        "Feasibility > Technical page — typically weekdays 09:00 to 17:00. " +
        "'None' on both start and end is the correct way to mark a day closed " +
        "(e.g. Sundays), and 00:00 to 23:00 means open around the clock. " +
        "Note the schedule dropdowns only save a value once you select it, so " +
        "re-pick the hours even if they already look right.";
    }
  }

  // The peak window decides WHICH tariff applies in each hour. Without it the
  // whole year would be priced at the non-peak rate and every saving would be
  // understated, so the backend refuses to run — catch it here instead, while
  // the user can still see which field is missing.
  const isSingleZonal = !String(tariffs.billingScheme || "")
    .toLowerCase()
    .includes("bi");
  const hasHour = (value) =>
    value !== undefined &&
    value !== null &&
    String(value).trim() !== "" &&
    String(value).trim().toLowerCase() !== "none";

  if (dynamicTariff || (itemizedTariff && tariffs.itemized?.pricingMode === SINGLE_ENERGY_RATE)) {
    // No peak window on an hourly price or a single all-day itemized rate.
  } else if (isSingleZonal) {
    if (!hasHour(tariffs.single?.peakStart) || !hasHour(tariffs.single?.peakEnd)) {
      errors.peakWindow =
        "Set the peak-hour window (Peak start and Peak end) on the " +
        "Feasibility > Technical page under Demand & Tariffs. Without it every " +
        "hour of the year is priced at the non-peak tariff and the savings are " +
        "understated.";
    }
  } else {
    const seasonHasBand = (season) => {
      const block = tariffs[season] || {};
      return (
        (hasHour(block.dayStart) && hasHour(block.dayEnd)) ||
        (hasHour(block.nightStart) && hasHour(block.nightEnd))
      );
    };
    if (!seasonHasBand("winter") || !seasonHasBand("summer")) {
      errors.peakWindow =
        "Set the winter and summer non-peak hour bands on the " +
        "Feasibility > Technical page under Demand & Tariffs. Without them every " +
        "hour of the year is priced at the non-peak tariff and the savings are " +
        "understated.";
    }
  }

  // Virtual net billing switches peak shaving off (the supplies draw all of
  // their consumption from the grid), so its rate cannot block the run.
  const virtualNb = isVirtualNetBilling(btm);
  if (btm.demandCharge?.enabled && !virtualNb) {
    // Each period carries its own rate. They are not interchangeable: an
    // annual rate divided by twelve and applied monthly gives a different
    // number, because twelve monthly maxima exceed one annual maximum.
    const annual = btm.demandCharge.measurementPeriod === "annual";
    const rate = Number(
      annual ? btm.demandCharge.eurPerKwYear : btm.demandCharge.eurPerKwMonth
    );
    if (!Number.isFinite(rate) || rate <= 0) {
      errors.demandChargeRate = annual
        ? "This scheme bills once on the annual maximum, so it needs a " +
          "positive rate in EUR/kW-year."
        : "Peak shaving needs a positive capacity charge in EUR/kW-month, " +
          "otherwise there is nothing for the battery to save.";
    }
  }

  // Zero feed-in forces the fixed feed-in and net billing off, so neither of
  // their prices can block the run — they are not sent.
  // Virtual net billing settles the surplus itself and forces both off too.
  const zeroFeedIn = isZeroFeedIn(btm) || virtualNb;

  // The fixed feed-in price is only required when net billing is NOT taking
  // over the injected energy (net billing supersedes it — see below).
  if (!zeroFeedIn && btm.surplus?.treatment === "fixed_price" && !btm.netBilling?.enabled) {
    const rawPrice = btm.surplus.feedInEurPerMwh;
    const price = Number(rawPrice);
    if (rawPrice == null || String(rawPrice).trim() === "" || !Number.isFinite(price) || price < 0) {
      errors.feedInPrice = SURPLUS_PRICE_REQUIRED_TEXT;
    }
  }

  // Net billing: a fixed agreed injection price needs a positive value. The
  // market (DAM) basis needs no price. Country defaults fill a blank basis, so
  // resolve it the same way the backend does before validating.
  if (!zeroFeedIn && btm.netBilling?.enabled) {
    const country = btm.netBilling.country || cfg?.biddingZone || "";
    const nbDefaults = netBillingCountryDefaults(country);
    const basisNb = btm.netBilling.injectionPriceBasis || nbDefaults.injectionPriceBasis;
    if (basisNb === "fixed_agreed") {
      const nbPrice = Number(btm.netBilling.fixedInjectionPriceEurPerMwh);
      if (!Number.isFinite(nbPrice) || nbPrice <= 0) {
        errors.netBillingFixedPrice =
          "Net billing is set to a fixed agreed injection price, so a positive " +
          "price (EUR/MWh) is required — otherwise injected surplus would be " +
          "credited at zero. Choose the day-ahead market basis instead if the " +
          "price is not fixed.";
      }
    }
  }

  // A standalone battery always charges from the grid, so retail ToU
  // arbitrage is always on for it (locked on the General page, forced in the
  // payload and by the backend): it can never be left with no stream to earn.

  // "Export from PV only" needs PV: for a standalone battery the regime that
  // says the battery never injects is zero feed-in.
  if (isStandaloneBess && isPvOnlyExport(btm)) {
    errors.gridInjection = PV_ONLY_EXPORT_STANDALONE_TEXT;
  }

  const escalation = Number(btm.retailTariff?.escalationPctYear);
  if (btm.retailTariff?.escalationPctYear !== "" && !Number.isFinite(escalation)) {
    errors.escalation = "Retail escalation must be a number (percent per year).";
  }
  const energyShareRaw = btm.retailTariff?.energySharePct;
  if (energyShareRaw !== "" && energyShareRaw !== null && energyShareRaw !== undefined) {
    const share = Number(energyShareRaw);
    if (!Number.isFinite(share) || share < 0 || share > 100) {
      errors.energyShare = ENERGY_SHARE_INVALID_TEXT;
    }
  }
  const regulatedRaw = btm.retailTariff?.regulatedEscalationPctYear;
  if (regulatedRaw !== "" && regulatedRaw !== null && regulatedRaw !== undefined
      && !Number.isFinite(Number(regulatedRaw))) {
    errors.regulatedEscalation = REGULATED_ESCALATION_INVALID_TEXT;
  }

  // A custom wear price is a number the run needs; the backend would fall
  // back to the derived default with a warning, which is not what a user who
  // chose "enter a price" meant.
  if (btm.wearCost?.mode === "custom") {
    const raw = btm.wearCost.eurPerMwh;
    const wear = Number(raw);
    if (raw === "" || raw === null || raw === undefined || !Number.isFinite(wear) || wear < 0) {
      errors.wearCost = WEAR_COST_CUSTOM_REQUIRED_TEXT;
    }
  }

  if (virtualNb) {
    Object.assign(errors, validateVirtualNetBilling(btm, cfg, { standalone: isStandaloneBess }));
  }

  return errors;
}

/** Non-blocking advisories shown inline; mirrors the backend's warning list. */
export function btmWarnings(btm, cfg) {
  const warnings = [];
  if (!btm || !isBtmSettlement(cfg?.settlementType)) return warnings;
  const isStandaloneBess = isStandaloneBessSystem(cfg);
  const zeroFeedIn = isZeroFeedIn(btm);

  if (zeroFeedIn) {
    // Every sentence here is number-free on purpose: the app translates whole
    // text nodes, so a figure interpolated into a sentence would leave the
    // whole sentence in English. The figures are in the fields themselves;
    // the backend repeats these checks with the numbers in its own warnings.
    if (cfg?.allowGridInjection !== false) {
      warnings.push(ZERO_FEED_IN_RUN_NOTE);
    }
    const agreedKw = Number(cfg?.grid?.importLimitKw);
    const pvKw = Number(cfg?.pvInstalledCap?.kw);
    if (!isStandaloneBess && Number.isFinite(agreedKw) && agreedKw > 0 && Number.isFinite(pvKw) && pvKw > agreedKw) {
      warnings.push(
        "The PV installed capacity exceeds the grid draw limit on the BESS page, " +
          "which stands for the agreed supply power. Under zero feed-in the " +
          "station may be sized up to 100% of the agreed supply power; as sized, " +
          "the connection would not be granted."
      );
    }
    if ((btm.touArbitrage?.enabled || isStandaloneBess) && cfg?.bess?.topology !== "dc-coupled") {
      warnings.push(ZERO_FEED_IN_TOU_NOTE);
    }
    if (!zeroFeedInCountryDefaults(btm.gridInjection?.country || cfg?.biddingZone).documented) {
      warnings.push(
        "No documented national zero feed-in framework is bundled for this " +
          "country. No export is modelled either way, but the connection terms " +
          "are user-supplied assumptions and are labelled as such in the report."
      );
    }
  }

  const pvOnly = isPvOnlyExport(btm);
  // The BESS page's connection-point injection switch follows from these settings. If
  // the stored value is not the implied one (an older project, a system-type
  // change), say what the run will do instead.
  const injection = btmGridInjectionState(btm, cfg);
  const storedInjection = cfg?.allowGridInjection !== false;
  const virtualNb = isVirtualNetBilling(btm);
  if (!zeroFeedIn && storedInjection !== injection.on) {
    warnings.push(
      injection.on
        ? virtualNb
          ? VNB_INJECTION_RUN_NOTE
          : pvOnly
            ? PV_ONLY_EXPORT_RUN_NOTE
            : BTM_INJECTION_ON_RUN_NOTE
        : BTM_INJECTION_OFF_RUN_NOTE
    );
  }
  if (pvOnly && !isStandaloneBess) {
    if (btm.surplus?.treatment !== "fixed_price" && !btm.netBilling?.enabled) {
      warnings.push(PV_ONLY_EXPORT_UNPRICED_TEXT);
    }
    if (btm.touArbitrage?.enabled && cfg?.bess?.topology !== "dc-coupled") {
      warnings.push(PV_ONLY_EXPORT_TOU_NOTE);
    }
    if (!pvOnlyExportCountryDefaults(btm.gridInjection?.country || cfg?.biddingZone).documented) {
      warnings.push(
        "No documented national framework for storage that does not inject is " +
          "bundled for this country. The battery is confined to the site load " +
          "either way, but the connection terms are user-supplied assumptions " +
          "and are labelled as such in the report."
      );
    }
  }

  if (!zeroFeedIn && !virtualNb && isStandaloneBess && btm.surplus?.treatment === "fixed_price") {
    warnings.push(
      "This is a standalone BESS with no generation, so there is no surplus to " +
        "export; the export stream will be ignored."
    );
  }
  // Net billing is hidden (and not sent) for a standalone battery.
  if (!zeroFeedIn && !virtualNb && !isStandaloneBess && btm.netBilling?.enabled) {
    if (btm.surplus?.treatment === "fixed_price") {
      warnings.push(
        "Both net billing and a fixed feed-in price are set. They are two " +
          "treatments of the same injected energy; net billing takes precedence " +
          "and the fixed feed-in will be ignored."
      );
    }
    const country = btm.netBilling.country || cfg?.biddingZone || "";
    if (!netBillingCountryDefaults(country).documented) {
      warnings.push(
        "No documented national net-billing framework is bundled for this " +
          "country. The net-billing parameters are user-supplied assumptions and " +
          "are labelled as such in the report — verify them against the national " +
          "regulation."
      );
    }
  }
  // The grid-charging consequence of ENABLING time-of-use arbitrage is already
  // stated inline next to that toggle, so it is deliberately not repeated here.
  // The OFF case has no inline alert, so it is surfaced below.
  const touBlocked = touArbitrageBlockedByFramework(btm, cfg);
  if (touBlocked && btm.touArbitrage?.enabled) {
    warnings.push(TOU_GR_INJECTING_RUN_NOTE);
  } else if (!btm.touArbitrage?.enabled && !isStandaloneBess && !touBlocked && !virtualNb) {
    warnings.push(
      "With time-of-use arbitrage off, the battery charges from surplus " +
        "generation only and 'Enable Grid Charging' will be switched OFF."
    );
  }
  emptyConsumptionMonths(cfg).forEach((month) => warnings.push(emptyMonthWarning(month)));
  if (btm.wearCost?.mode === "custom") {
    const wear = Number(btm.wearCost.eurPerMwh);
    if (Number.isFinite(wear) && wear > 200) {
      warnings.push(WEAR_COST_VERY_HIGH_TEXT);
    }
  }
  const escalation = Number(btm.retailTariff?.escalationPctYear);
  if (Number.isFinite(escalation) && escalation > 15) {
    warnings.push(
      `A retail escalation of ${escalation}%/year is very high and will ` +
        "dominate the savings projection — confirm the unit is percent per year."
    );
  }
  return warnings;
}

/**
 * Shape the block for the run payload. Numeric strings become numbers so the
 * backend normalizer receives the same types it would from a saved config.
 */
export function buildBtmPayload(btm, { standalone = false, country = "" } = {}) {
  const normalized = normalizeBtm({ btm });
  const touBlocked = touArbitrageBlockedByFramework(normalized, null, { standalone, country });
  const zeroFeedIn = isZeroFeedIn(normalized);
  // Virtual net billing settles the surplus itself: peak shaving, ToU, the
  // fixed feed-in and net billing are off (backend btm.py does the same).
  const virtualNb = isVirtualNetBilling(normalized);
  // A standalone battery has no PV surplus to sell or net, and it always
  // charges from the grid, so retail ToU arbitrage is always on for it. The
  // General page hides those sections for it; the payload must not carry
  // their hidden, stored values into the run.
  const noSurplus = zeroFeedIn || virtualNb || Boolean(standalone);
  const num = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return {
    retailTariff: {
      escalationPctYear: num(normalized.retailTariff.escalationPctYear),
      // Blank stays blank (null): 0 would mean "no energy in the price".
      energySharePct:
        normalized.retailTariff.energySharePct === "" ||
        normalized.retailTariff.energySharePct === null ||
        normalized.retailTariff.energySharePct === undefined ||
        !Number.isFinite(Number(normalized.retailTariff.energySharePct))
          ? null
          : Number(normalized.retailTariff.energySharePct),
      regulatedEscalationPctYear:
        normalized.retailTariff.regulatedEscalationPctYear === "" ||
        normalized.retailTariff.regulatedEscalationPctYear === null ||
        normalized.retailTariff.regulatedEscalationPctYear === undefined ||
        !Number.isFinite(Number(normalized.retailTariff.regulatedEscalationPctYear))
          ? null
          : Number(normalized.retailTariff.regulatedEscalationPctYear),
    },
    demandCharge: {
      enabled: !virtualNb && Boolean(normalized.demandCharge.enabled),
      eurPerKwMonth: num(normalized.demandCharge.eurPerKwMonth),
      eurPerKwYear: num(normalized.demandCharge.eurPerKwYear),
      measurementPeriod: normalized.demandCharge.measurementPeriod || "monthly",
      measurementInterval:
        normalized.demandCharge.measurementInterval || "hourly",
      basis: normalized.demandCharge.basis,
    },
    touArbitrage: {
      enabled: !virtualNb && (Boolean(standalone) || (!touBlocked && Boolean(normalized.touArbitrage.enabled))),
    },
    // Wear price: the mode always; the figure only when the user entered one
    // (the backend derives the default itself from the battery it runs with).
    wearCost: {
      mode: normalized.wearCost.mode,
      eurPerMwh:
        normalized.wearCost.mode === "custom" ? num(normalized.wearCost.eurPerMwh) : null,
    },
    gridInjection: {
      mode: normalized.gridInjection.mode,
      country: String(normalized.gridInjection.country || "").trim().toUpperCase(),
    },
    // Zero feed-in: nothing is ever exported, so the surplus is curtailed and
    // net billing is off in the payload itself — the backend enforces the
    // same, this just keeps what is sent honest about what will run.
    surplus: {
      treatment: noSurplus ? "none" : normalized.surplus.treatment,
      feedInEurPerMwh: noSurplus ? 0 : num(normalized.surplus.feedInEurPerMwh),
    },
    // Net billing: send only what the user set; the backend fills a blank
    // country from the project bidding zone and blank enums from the country
    // defaults. The fixed price is numeric; blank -> 0.
    netBilling: {
      enabled: noSurplus ? false : Boolean(normalized.netBilling.enabled),
      country: String(normalized.netBilling.country || "").trim().toUpperCase(),
      injectionPriceBasis: normalized.netBilling.injectionPriceBasis || "",
      fixedInjectionPriceEurPerMwh: num(normalized.netBilling.fixedInjectionPriceEurPerMwh),
      settlementPeriod: normalized.netBilling.settlementPeriod || "",
      surplusCreditTreatment: normalized.netBilling.surplusCreditTreatment || "",
      injectionRevenueRecipient:
        normalized.netBilling.injectionRevenueRecipient || "host",
      thirdPartyInvestorPriceEurPerMwh: num(
        normalized.netBilling.thirdPartyInvestorPriceEurPerMwh
      ),
    },
    // Sent only under virtual net billing, so every other regime's payload is
    // exactly what it was.
    ...(virtualNb ? { virtualNetBilling: buildVirtualNetBillingPayload(normalized.virtualNetBilling) } : {}),
  };
}
